import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ArrowLeft, MapPin, Eye, Calendar, Tag, Share2, Star, ExternalLink, Heart, Copy, Check, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { DealTimerFull } from '../components/DealTimer';
import ClaimCodeDisplay from '../components/ClaimCode';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_COLORS = {
  'Food & Drink': '#C8A97E',
  'Nightlife': '#8A9A8B',
  'Fitness & Wellness': '#7BA05B',
  'Activities': '#A07BC8',
  'Free Deals': '#C87B7B',
  'Convenience Stores': '#5B9EA0',
  'Liquor Stores': '#8B4A62',
};

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

function StoreHoursDisplay({ hours }) {
  const today = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  return (
    <div className="mt-4 bg-[#F6F1E9] border border-[#C8A97E]/15 rounded-xl p-3" data-testid="store-hours-display">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Clock size={11} className="text-[#C8A97E]" />
        <span className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B]">Store Hours</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {DAYS.map(day => {
          const d = hours[day] || {};
          const isToday = day === today;
          const closed = d.closed;
          return (
            <div key={day} className={`flex items-center justify-between text-xs py-0.5 ${isToday ? 'font-bold text-[#2B2B2B]' : 'text-[#2B2B2B]/55'}`}>
              <span className="capitalize">{DAY_LABELS[day]}{isToday && <span className="ml-1 text-[#C8A97E] text-[10px]">•</span>}</span>
              <span className={closed ? 'text-red-400' : ''}>
                {closed ? 'Closed' : (d.open && d.close) ? `${d.open}–${d.close}` : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PromoCodeDisplay({ code, dealId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div className="bg-[#F6F1E9] border-2 border-dashed border-[#C8A97E]/60 rounded-2xl p-5 mb-4" data-testid="promo-code-section">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-2">Promo Code</p>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-bold text-[#2B2B2B] font-mono tracking-widest" data-testid="promo-code-value">{code}</p>
          <p className="text-xs text-[#2B2B2B]/50 mt-0.5">Show this code at the business to redeem</p>
        </div>
        <button
          onClick={handleCopy}
          data-testid="copy-promo-button"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
            copied
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-[#2B2B2B] text-[#F6F1E9] hover:bg-[#C8A97E]'
          }`}
        >
          {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>
    </div>
  );
}

export default function DealDetailPage() {
  const { dealId } = useParams();
  const { user, login } = useAuth();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/deals/${dealId}`)
      .then((r) => r.json())
      .then((data) => { setDeal(data); setLoading(false); })
      .catch(() => setLoading(false));
    // Check if saved in localStorage
    const savedDeals = JSON.parse(localStorage.getItem('perkly_saved') || '[]');
    setSaved(savedDeals.includes(dealId));
  }, [dealId]);

  const handleSave = async () => {
    setSaveLoading(true);
    const savedDeals = JSON.parse(localStorage.getItem('perkly_saved') || '[]');
    if (saved) {
      const updated = savedDeals.filter((id) => id !== dealId);
      localStorage.setItem('perkly_saved', JSON.stringify(updated));
      setSaved(false);
    } else {
      savedDeals.push(dealId);
      localStorage.setItem('perkly_saved', JSON.stringify(savedDeals));
      setSaved(true);
    }
    // Also sync with backend if logged in
    if (user) {
      try {
        await fetch(`${API}/users/saved/${dealId}`, {
          method: saved ? 'DELETE' : 'POST',
          credentials: 'include',
        });
      } catch {}
    }
    setSaveLoading(false);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1E9]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-[#F6F1E9]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <p className="text-5xl mb-4">😕</p>
          <h2 className="text-2xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Deal Not Found</h2>
          <Link to="/explore" className="btn-primary inline-flex">Back to Explore</Link>
        </div>
      </div>
    );
  }

  const categoryColor = CATEGORY_COLORS[deal.category] || '#C8A97E';

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <SEO
        title={deal ? `${deal.title} at ${deal.business_name}` : 'Deal Details'}
        description={deal?.description || `Exclusive student deal at ${deal?.business_name || 'a local Athens business'}. Claim with Perkly.`}
        canonical={`/deals/${dealId}`}
      />
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back */}
        <Link to="/explore" className="inline-flex items-center gap-2 text-sm text-[#2B2B2B]/60 hover:text-[#C8A97E] mb-6 transition-colors" data-testid="back-to-explore">
          <ArrowLeft size={16} /> Back to Explore
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left — Image + Info — shown BELOW claim card on mobile */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden aspect-[16/9] mb-6 shadow-lg" data-testid="deal-detail-image">
              <img
                src={deal.image_url}
                alt={deal.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80'; }}
              />
              <div className="absolute top-4 left-4 flex gap-2">
                <span
                  className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-white"
                  style={{ backgroundColor: categoryColor }}
                  data-testid="deal-category-badge"
                >
                  {deal.category}
                </span>
                {deal.is_featured && (
                  <span className="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-[#2B2B2B] text-[#F6F1E9] flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> Featured
                  </span>
                )}
                {deal.age_restriction === '21+' && (
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#8B1A1A] text-white" data-testid="deal-age-badge">
                    21+ Only
                  </span>
                )}
              </div>
              {deal.discount_text && (
                <div className="absolute top-4 right-4 bg-white rounded-full px-3 py-1.5">
                  <span className="text-sm font-bold text-[#C8A97E]" data-testid="deal-discount-text">{deal.discount_text}</span>
                </div>
              )}
            </div>

            {/* Title + Meta */}
            <div>
              <div className="flex items-start justify-between gap-3 mb-3">
                <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B]" data-testid="deal-business-name">
                  {deal.business_name}
                </p>
                {/* Save button */}
                <button
                  onClick={handleSave}
                  disabled={saveLoading}
                  data-testid="save-deal-button"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    saved
                      ? 'bg-red-50 border-red-200 text-red-500'
                      : 'bg-white border-[#8A9A8B]/30 text-[#2B2B2B] hover:border-[#C8A97E]'
                  }`}
                >
                  <Heart size={12} fill={saved ? 'currentColor' : 'none'} />
                  {saved ? 'Saved' : 'Save Deal'}
                </button>
              </div>
              <h1
                className="text-3xl sm:text-4xl font-bold text-[#2B2B2B] leading-tight mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="deal-title"
              >
                {deal.title}
              </h1>

              {/* Meta row */}
              <div className="flex flex-wrap gap-4 text-sm text-[#2B2B2B]/60 mb-6">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#C8A97E]" />
                  <span data-testid="deal-city">{deal.city_name}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Eye size={14} className="text-[#C8A97E]" />
                  {deal.views} views
                </span>
                {deal.start_date && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} className="text-[#C8A97E]" />
                    {deal.start_date} {deal.end_date ? `– ${deal.end_date}` : ''}
                  </span>
                )}
              </div>

              {/* Countdown Timer */}
              {deal.end_date && <DealTimerFull endDate={deal.end_date} />}

              {/* Description */}
              <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10 mb-6">
                <h3 className="font-bold text-[#2B2B2B] mb-3">About This Deal</h3>
                <p className="text-[#2B2B2B]/70 leading-relaxed text-sm" data-testid="deal-description">
                  {deal.description}
                </p>
              </div>

              {/* Tags */}
              {deal.tags && deal.tags.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="deal-tags">
                  {deal.tags.map((tag) => (
                    <span key={tag} className="flex items-center gap-1 text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-3 py-1 rounded-full">
                      <Tag size={10} /> {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right — CTA Card — shown FIRST on mobile, right on desktop */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="sticky top-24 bg-white rounded-3xl p-6 border border-[#8A9A8B]/10 shadow-md" data-testid="deal-cta-card">
              {/* Price */}
              <div className="text-center mb-6 pb-6 border-b border-[#8A9A8B]/10">
                {deal.price !== null && deal.price !== undefined ? (
                  <div>
                    <p className="text-5xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {deal.price === 0 ? 'FREE' : `$${deal.price}`}
                    </p>
                    {deal.original_price && deal.original_price > 0 && (
                      <p className="text-lg text-[#2B2B2B]/40 line-through mt-1">${deal.original_price}</p>
                    )}
                    {deal.discount_text && (
                      <span className="inline-block mt-2 bg-[#C8A97E]/15 text-[#C8A97E] text-sm font-bold px-3 py-1 rounded-full">
                        {deal.discount_text}
                      </span>
                    )}
                  </div>
                ) : deal.discount_text ? (
                  <div>
                    <p className="text-4xl font-bold text-[#C8A97E]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {deal.discount_text}
                    </p>
                  </div>
                ) : null}
              </div>

              {/* CTA Button */}
              {deal.age_restriction === '21+' && (
                <div className="mb-4 rounded-2xl bg-[#8B1A1A]/8 border border-[#8B1A1A]/25 p-4" data-testid="age-restriction-notice">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#8B1A1A] text-white text-xs font-bold flex items-center justify-center mt-0.5">21+</span>
                    <div>
                      <p className="text-sm font-bold text-[#8B1A1A] mb-1">Age Verification Required</p>
                      <p className="text-xs text-[#2B2B2B]/65 leading-relaxed">
                        You must be 21 or older to redeem this deal. Staff at <strong>{deal.business_name}</strong> will verify your ID at time of redemption. This policy is set by the business and is their sole responsibility.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {user ? (
                <div className="text-center mb-4">
                  <div className="bg-[#F6F1E9] rounded-2xl p-4 mb-4">
                    <p className="text-sm font-semibold text-[#2B2B2B] mb-1">How to Redeem</p>
                    <p className="text-xs text-[#2B2B2B]/60 leading-relaxed">
                      Get your unique claim code below and show it at {deal.business_name}. Bring your student ID for student discounts.
                    </p>
                  </div>
                  <ClaimCodeDisplay deal={deal} onClaimed={() => {
                    setDeal(prev => ({ ...prev, claim_count: (prev.claim_count || 0) + 1 }));
                  }} />
                </div>
              ) : (
                <div className="text-center mb-4">
                  <p className="text-sm text-[#2B2B2B]/60 mb-4">Sign in to see full deal details and redeem</p>
                  <button
                    data-testid="deal-signin-button"
                    onClick={() => login(`/deals/${dealId}`)}
                    className="w-full btn-primary justify-center py-4 text-base"
                  >
                    Sign In to Claim
                  </button>
                </div>
              )}

              {/* Business info */}
              <div className="border-t border-[#8A9A8B]/10 pt-5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-3">Business</h4>
                <p className="font-bold text-[#2B2B2B] mb-2">{deal.business_name}</p>

                {/* Location banner — full address with Get Directions */}
                {deal.business_address ? (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(deal.business_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="business-address-banner"
                    className="flex items-start gap-2.5 bg-[#F6F1E9] border border-[#C8A97E]/20 rounded-xl p-3 mb-4 hover:bg-[#C8A97E]/12 hover:border-[#C8A97E]/40 transition-all group"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#C8A97E]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C8A97E]/35 transition-colors">
                      <MapPin size={13} className="text-[#C8A97E]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#2B2B2B] leading-snug">{deal.business_address}</p>
                      <p className="text-xs text-[#C8A97E] font-semibold mt-1 flex items-center gap-1">
                        Get Directions <ExternalLink size={10} />
                      </p>
                    </div>
                  </a>
                ) : (
                  <p className="text-xs text-[#2B2B2B]/55 flex items-center gap-1.5 mb-4">
                    <MapPin size={11} /> {deal.city_name}
                  </p>
                )}

                <Link
                  to={`/explore?category=${encodeURIComponent(deal.category)}`}
                  className="flex items-center gap-1.5 text-xs text-[#C8A97E] font-semibold hover:gap-2 transition-all"
                >
                  More deals like this <ExternalLink size={11} />
                </Link>

                {/* Store Hours — only shown when the business has set hours */}
                {deal.store_hours && Object.values(deal.store_hours).some(d => !d.closed && (d.open || d.close)) && (
                  <StoreHoursDisplay hours={deal.store_hours} />
                )}
              </div>

              {/* Share */}
              <button
                data-testid="share-deal-button"
                onClick={handleShare}
                className="w-full mt-5 flex items-center justify-center gap-2 py-3 rounded-full border border-[#8A9A8B]/30 text-sm font-semibold text-[#2B2B2B] hover:border-[#C8A97E] transition-colors"
              >
                <Share2 size={14} />
                {copied ? 'Link Copied!' : 'Share Deal'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
